import { EntityType } from '@exam-management/graphql';
import {
  CommentArea,
  QuestionCommentFormProvider,
  QuestionCommentProvider,
  ReviewResultForm,
  ReviewResultFormProvider,
  ReviewResultProvider,
  useQuestionComment,
  useQSetReviewSubtask,
  useReviewResult,
} from '@exam-management/qset-review-subtask-details';
import { useParams } from '@exam-management/router';
import { mappedQSetReviewSubtaskStatus } from '@exam-management/task-management';
import { Box, Flex, FormLabel, PaperSection, Skeleton, Text } from '@shared/components';
import { matchPath, useLocation } from 'react-router-dom';

import { EmptyComment } from '../../components/empty-comment';
import { EmptyReviewResult } from '../../components/empty-review-result';
import { getReviewResultVariant, ReviewResultVariant } from '../../utils';

interface ReviewResultQuestionContentProps {
  variant: ReviewResultVariant.EDITABLE | ReviewResultVariant.READONLY;
  taskId: string;
  entityId: string;
  entityType: EntityType;
}

function ReviewResultQuestionContent(props: Readonly<ReviewResultQuestionContentProps>) {
  const { variant, taskId, entityId, entityType } = props;
  const { verdict } = useReviewResult();
  const { comments } = useQuestionComment();

  const hasVerdict = verdict.data != null;
  const hasComments = comments.data.length > 0;
  const isLoading = verdict.loading || comments.loading;

  if (isLoading) {
    return (
      <Flex
        sx={(theme) => ({ gap: theme.spacing(4), flexDirection: 'column', p: theme.spacing(6) })}
      >
        <Skeleton height={52} />
        <Skeleton height={52} />
      </Flex>
    );
  }

  if (variant === ReviewResultVariant.EDITABLE) {
    return (
      <Flex
        sx={(theme) => ({ gap: theme.spacing(6), flexDirection: 'column', p: theme.spacing(6) })}
      >
        <Box>
          <FormLabel required sx={{ mb: (theme) => theme.spacing(1) }}>
            ผลการทบทวน
          </FormLabel>
          <ReviewResultFormProvider>
            <ReviewResultForm variant={ReviewResultVariant.EDITABLE} />
          </ReviewResultFormProvider>
        </Box>
        <Box>
          <Text variant='h4' sx={{ mb: (theme) => theme.spacing(4) }}>
            ความคิดเห็น
          </Text>
          <QuestionCommentFormProvider taskId={taskId} entityId={entityId} entityType={entityType}>
            <CommentArea comments={comments.data} taskId={taskId} />
          </QuestionCommentFormProvider>
        </Box>
      </Flex>
    );
  }

  // READONLY branch

  if (!hasVerdict) {
    return (
      <Box sx={(theme) => ({ p: theme.spacing(6) })}>
        <EmptyReviewResult />
      </Box>
    );
  }

  return (
    <Flex sx={(theme) => ({ gap: theme.spacing(6), flexDirection: 'column', p: theme.spacing(6) })}>
      <Box>
        <FormLabel required sx={{ mb: (theme) => theme.spacing(1) }}>
          ผลการทบทวน
        </FormLabel>
        <ReviewResultFormProvider>
          <ReviewResultForm variant={ReviewResultVariant.READONLY} />
        </ReviewResultFormProvider>
      </Box>
      <Box>
        <Text variant='h4' sx={{ mb: (theme) => theme.spacing(4) }}>
          ความคิดเห็น
        </Text>
        {hasComments ? (
          <QuestionCommentFormProvider taskId={taskId} entityId={entityId} entityType={entityType}>
            <CommentArea isReadonly comments={comments.data} taskId={taskId} />
          </QuestionCommentFormProvider>
        ) : (
          <EmptyComment />
        )}
      </Box>
    </Flex>
  );
}

export function ReviewResultQuestion() {
  const location = useLocation();
  const matchedTaskManagement = matchPath(
    '/task-management/qset-review/:taskId/*',
    location.pathname,
  );
  const paramPath = matchedTaskManagement
    ? '/task-management/qset-review/:taskId/subtask/:subTaskId/task-details/exam-details'
    : '/my-task/qset-review/:taskId/subtask/:subTaskId/task-details/exam-details';
  const { subTaskId = '' } = useParams(paramPath);

  const {
    roles,
    status,
    qSetQuestion: { currentQuestion },
  } = useQSetReviewSubtask();

  const taskStatus =
    mappedQSetReviewSubtaskStatus[status.code as keyof typeof mappedQSetReviewSubtaskStatus];
  const variant = getReviewResultVariant(roles, taskStatus);
  if (!currentQuestion || variant === null) {
    return null;
  }

  if (variant === ReviewResultVariant.HIDDEN) {
    return (
      <PaperSection title='Review Result'>
        <Box sx={(theme) => ({ p: theme.spacing(6) })}>
          <EmptyReviewResult />
        </Box>
      </PaperSection>
    );
  }

  return (
    <PaperSection title='Review Result'>
      <ReviewResultProvider
        taskId={subTaskId}
        entityId={currentQuestion.id}
        entityType={EntityType.Question}
      >
        <QuestionCommentProvider
          taskId={subTaskId}
          entityId={currentQuestion.id}
          entityType={EntityType.Question}
        >
          <ReviewResultQuestionContent
            variant={variant}
            taskId={subTaskId}
            entityId={currentQuestion.id}
            entityType={EntityType.Question}
          />
        </QuestionCommentProvider>
      </ReviewResultProvider>
    </PaperSection>
  );
}
